import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Search, FileText, ExternalLink, Loader2, CalendarDays, TimerReset, Building2 } from "lucide-react";
import { toast } from "sonner";

const CURRENT_YEAR = new Date().getFullYear();
const MONTHS = [
  "Ianuarie", "Februarie", "Martie", "Aprilie", "Mai", "Iunie",
  "Iulie", "August", "Septembrie", "Octombrie", "Noiembrie", "Decembrie",
];

const FRECVENTA_LABEL = { 1: "Lunar", 3: "La 3 luni", 12: "La 12 luni" };
const STATUS_META = {
  active:         { label: "Activ",         color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  expiring_soon:  { label: "Expiră curând", color: "bg-amber-100 text-amber-900 border-amber-300" },
  expired:        { label: "Expirat",       color: "bg-red-100 text-red-800 border-red-300" },
  unknown:        { label: "—",              color: "bg-slate-100 text-slate-600 border-slate-300" },
};

export default function ContractsList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [year, setYear] = useState("all");
  const [month, setMonth] = useState("all");
  const [frecventa, setFrecventa] = useState(0);
  const [status, setStatus] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const params = {
        year: year === "all" ? undefined : year,
        month: month === "all" ? undefined : month,
        frecventa: frecventa || undefined,
        status: status === "all" ? undefined : status,
        q: q || undefined,
      };
      const { data } = await api.get("/contracts", { params });
      setRows(data);
    } catch (e) {
      toast.error("Eroare la încărcare");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month, frecventa, status]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const yearOptions = useMemo(() => {
    const s = new Set([CURRENT_YEAR]);
    rows.forEach((r) => {
      const y = parseInt((r.data || "").slice(0, 4), 10);
      if (y) s.add(y);
    });
    return Array.from(s).sort((a, b) => b - a);
  }, [rows]);

  const stats = useMemo(() => {
    const s = { total: rows.length, active: 0, soon: 0, expired: 0 };
    rows.forEach((r) => {
      if (r.status_expira === "active") s.active++;
      else if (r.status_expira === "expiring_soon") s.soon++;
      else if (r.status_expira === "expired") s.expired++;
    });
    return s;
  }, [rows]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="label-eyebrow">Bază de date</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight">Contracte</h1>
          <p className="text-sm text-muted-foreground mt-1" data-testid="contracts-count">
            {loading ? "Se încarcă..." : `${stats.total} contracte afișate`}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <StatChip color="emerald" label="Active" value={stats.active} />
          <StatChip color="amber"   label="Expiră curând" value={stats.soon} />
          <StatChip color="red"     label="Expirate" value={stats.expired} />
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Caută după număr contract, denumire client sau CUI..."
            className="pl-9 h-10 bg-white"
            data-testid="contracts-search"
          />
        </div>
        <Select value={year} onValueChange={setYear}>
          <SelectTrigger className="w-full md:w-40 h-10 bg-white" data-testid="contracts-year-filter">
            <SelectValue placeholder="Toți anii" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toți anii</SelectItem>
            {yearOptions.map((y) => (<SelectItem key={y} value={String(y)}>{y}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={month} onValueChange={setMonth} disabled={year === "all"}>
          <SelectTrigger className="w-full md:w-40 h-10 bg-white" data-testid="contracts-month-filter">
            <SelectValue placeholder="Toate lunile" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toate lunile</SelectItem>
            {MONTHS.map((m, i) => (<SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-full md:w-44 h-10 bg-white" data-testid="contracts-status-filter">
            <SelectValue placeholder="Toate statusurile" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toate statusurile</SelectItem>
            <SelectItem value="active">Doar active</SelectItem>
            <SelectItem value="expiring_soon">Expiră curând (≤30 zile)</SelectItem>
            <SelectItem value="expired">Expirate</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Filtre pe frecvență */}
      <div className="flex flex-wrap items-center gap-2 -mt-2">
        <span className="label-eyebrow mr-1">Frecvență:</span>
        {[
          { v: 1, label: "La 1 lună" },
          { v: 3, label: "La 3 luni" },
          { v: 12, label: "La 12 luni" },
        ].map((f) => (
          <button
            key={f.v}
            type="button"
            onClick={() => setFrecventa(frecventa === f.v ? 0 : f.v)}
            data-testid={`contract-filter-freq-${f.v}`}
            className={`text-xs font-medium border rounded-full px-3 py-1.5 transition-colors ${
              frecventa === f.v ? "border-gold bg-gold text-white" : "border-border bg-white hover:border-gold"
            }`}
          >
            {frecventa === f.v ? "☑ " : "☐ "}{f.label}
          </button>
        ))}
        {(frecventa || status !== "all" || q) && (
          <button
            type="button"
            onClick={() => { setFrecventa(0); setStatus("all"); setQ(""); }}
            className="text-xs text-muted-foreground hover:text-foreground underline"
          >
            șterge filtrele
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white border border-border rounded-lg overflow-hidden">
        <Table>
          <TableHeader className="bg-slate-50">
            <TableRow>
              <TableHead className="label-eyebrow text-foreground">Nr. Contract</TableHead>
              <TableHead className="label-eyebrow text-foreground">Client</TableHead>
              <TableHead className="label-eyebrow text-foreground hidden md:table-cell">CUI</TableHead>
              <TableHead className="label-eyebrow text-foreground hidden lg:table-cell">Adresa contract</TableHead>
              <TableHead className="label-eyebrow text-foreground">Data</TableHead>
              <TableHead className="label-eyebrow text-foreground hidden md:table-cell">Frecvență</TableHead>
              <TableHead className="label-eyebrow text-foreground">Expiră</TableHead>
              <TableHead className="label-eyebrow text-foreground">Status</TableHead>
              <TableHead className="text-right"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-10 text-muted-foreground">
                  <Loader2 className="w-5 h-5 mx-auto animate-spin" />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-16 text-muted-foreground">
                  <FileText className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  Niciun contract găsit cu filtrele curente.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((c) => {
                const meta = STATUS_META[c.status_expira] || STATUS_META.unknown;
                return (
                  <TableRow key={c.id} data-testid={`contract-row-${c.id}`} className="hover:bg-amber-50/30">
                    <TableCell className="font-mono font-semibold text-gold-deep">{c.numar || "—"}</TableCell>
                    <TableCell className="font-medium">{c.client_denumire}</TableCell>
                    <TableCell className="font-mono text-xs hidden md:table-cell">{c.client_cui || "—"}</TableCell>
                    <TableCell className="text-xs text-muted-foreground hidden lg:table-cell max-w-[260px]" title={c.locatie || c.client_localitate || ""}>
                      {c.locatie ? (
                        <span className="line-clamp-2 break-words">{c.locatie}</span>
                      ) : (
                        <span className="italic opacity-70">{c.client_localitate || "—"}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs font-mono">{c.data ? new Date(c.data).toLocaleDateString("ro-RO") : "—"}</TableCell>
                    <TableCell className="text-xs hidden md:table-cell">{FRECVENTA_LABEL[c.frecventa_luni] || "—"}</TableCell>
                    <TableCell className="text-xs font-mono">
                      {c.expira_la ? (
                        <div>
                          <div>{new Date(c.expira_la).toLocaleDateString("ro-RO")}</div>
                          {c.expira_zile !== null && c.expira_zile !== undefined && (
                            <div className={`text-[10px] ${c.expira_zile < 0 ? "text-red-600" : c.expira_zile <= 30 ? "text-amber-700" : "text-muted-foreground"}`}>
                              {c.expira_zile < 0 ? `expirat de ${-c.expira_zile} zile` : `în ${c.expira_zile} zile`}
                            </div>
                          )}
                        </div>
                      ) : "—"}
                    </TableCell>
                    <TableCell>
                      <span className={`inline-block text-[10px] font-bold uppercase tracking-widest border rounded-full px-2 py-1 ${meta.color}`}>
                        {meta.label}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <Link
                        to={`/clienti/${c.client_id}`}
                        className="inline-flex items-center gap-1 text-sm text-gold-deep hover:text-gold underline"
                        data-testid={`contract-open-${c.id}`}
                      >
                        Deschide client <ExternalLink className="w-3.5 h-3.5" />
                      </Link>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function StatChip({ color, label, value }) {
  const map = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    amber:   "bg-amber-50 border-amber-200 text-amber-900",
    red:     "bg-red-50 border-red-200 text-red-800",
  };
  return (
    <div className={`inline-flex items-center gap-2 border rounded-md px-3 py-1.5 text-xs font-semibold ${map[color]}`}>
      <span className="uppercase tracking-widest text-[10px] opacity-70">{label}</span>
      <span className="font-black text-base">{value}</span>
    </div>
  );
}
